/**
 * Country centroids (approx. geographic centre, lat/lng) for plotting the
 * harvested pool on a world map. The harvester tags each org with a full
 * country `region` (matching xop-agents `_WORLD_REGIONS`) and an ISO-2-ish
 * `country` — we resolve a centroid from either. Whole-degree precision is
 * fine for a country-level dot.
 *
 * Keyed by LOWERCASED country name. `centroidFor` also accepts ISO-2 codes and
 * a few common aliases (USA/UK/…), so legacy rows still place.
 */
export type LatLng = [number, number];

export const COUNTRY_CENTROIDS: Record<string, LatLng> = {
  // North America
  "united states": [39.8, -98.6], canada: [56.1, -106.3], mexico: [23.6, -102.5],
  // Europe
  "united kingdom": [54.0, -2.5], france: [46.6, 2.5], germany: [51.2, 10.4],
  spain: [40.2, -3.7], italy: [42.8, 12.6], netherlands: [52.2, 5.3],
  belgium: [50.6, 4.6], sweden: [62.2, 15.6], switzerland: [46.8, 8.2],
  austria: [47.6, 14.1], poland: [52.0, 19.1], ukraine: [48.4, 31.2],
  "czech republic": [49.8, 15.5], finland: [64.5, 26.0], norway: [64.5, 12.5],
  denmark: [56.0, 9.5], ireland: [53.2, -8.0], portugal: [39.5, -8.0],
  greece: [39.1, 22.9], hungary: [47.2, 19.4], romania: [45.9, 24.9],
  bulgaria: [42.7, 25.5], slovakia: [48.7, 19.7], slovenia: [46.1, 14.8],
  croatia: [45.1, 15.2], serbia: [44.0, 20.9], "bosnia and herzegovina": [44.0, 17.8],
  albania: [41.2, 20.0], "north macedonia": [41.6, 21.7], montenegro: [42.7, 19.4],
  kosovo: [42.6, 20.9], lithuania: [55.2, 23.9], latvia: [56.9, 24.6],
  estonia: [58.6, 25.0], belarus: [53.7, 28.0], moldova: [47.4, 28.4],
  iceland: [64.9, -18.6], luxembourg: [49.8, 6.1], malta: [35.9, 14.4],
  cyprus: [35.1, 33.4], russia: [61.5, 90.0],
  // Middle East
  "united arab emirates": [23.9, 54.0], "saudi arabia": [23.9, 45.1],
  qatar: [25.3, 51.2], kuwait: [29.3, 47.5], bahrain: [26.0, 50.5],
  oman: [21.0, 57.0], israel: [31.0, 34.9], turkey: [39.0, 35.2],
  iran: [32.4, 53.7], iraq: [33.0, 43.7], jordan: [31.2, 36.5],
  lebanon: [33.9, 35.9], syria: [35.0, 38.5], yemen: [15.6, 48.0],
  // Asia
  china: [35.9, 104.2], japan: [36.2, 138.3], "south korea": [36.5, 127.8],
  india: [22.4, 79.0], indonesia: [-2.5, 118.0], pakistan: [30.4, 69.3],
  bangladesh: [23.7, 90.4], vietnam: [16.2, 107.8], thailand: [15.1, 101.0],
  philippines: [12.9, 121.8], malaysia: [4.2, 101.9], singapore: [1.35, 103.8],
  taiwan: [23.8, 121.0], "hong kong": [22.35, 114.1], myanmar: [21.9, 95.9],
  cambodia: [12.5, 104.9], laos: [19.9, 102.5], "sri lanka": [7.9, 80.7],
  nepal: [28.4, 84.1], kazakhstan: [48.0, 67.3], uzbekistan: [41.4, 63.6],
  turkmenistan: [39.0, 59.6], azerbaijan: [40.3, 47.7], georgia: [42.3, 43.4],
  armenia: [40.3, 45.0], kyrgyzstan: [41.2, 74.8], tajikistan: [38.9, 71.3],
  afghanistan: [33.9, 67.7], mongolia: [46.9, 103.8], "north korea": [40.3, 127.5],
  brunei: [4.5, 114.7], bhutan: [27.4, 90.4], maldives: [3.2, 73.0],
  // Oceania
  australia: [-25.7, 134.5], "new zealand": [-41.8, 172.5],
  "papua new guinea": [-6.5, 147.2], fiji: [-17.7, 178.0],
  "solomon islands": [-9.6, 160.2], vanuatu: [-15.4, 166.9], samoa: [-13.8, -172.1],
  tonga: [-21.2, -175.2], kiribati: [1.4, 173.0], micronesia: [6.9, 158.2],
  palau: [7.5, 134.6], "marshall islands": [7.1, 171.2], nauru: [-0.5, 166.9],
  tuvalu: [-7.1, 177.6],
  // Latin America & Caribbean
  brazil: [-10.8, -52.9], argentina: [-38.4, -63.6], chile: [-35.7, -71.5],
  colombia: [4.6, -74.3], peru: [-9.2, -75.0], venezuela: [6.4, -66.6],
  ecuador: [-1.4, -78.2], bolivia: [-16.3, -63.6], paraguay: [-23.4, -58.4],
  uruguay: [-32.5, -55.8], guyana: [4.9, -58.9], suriname: [4.0, -56.0],
  panama: [8.4, -80.1], "costa rica": [9.7, -83.8], guatemala: [15.6, -90.2],
  honduras: [14.8, -86.6], "el salvador": [13.8, -88.9], nicaragua: [12.9, -85.2],
  belize: [17.2, -88.6], cuba: [21.5, -79.5], "dominican republic": [18.7, -70.2],
  haiti: [19.0, -72.3], jamaica: [18.1, -77.3], "trinidad and tobago": [10.7, -61.2],
  bahamas: [25.0, -77.4], barbados: [13.2, -59.5],
  // Africa
  "south africa": [-29.0, 24.7], egypt: [26.8, 30.8], nigeria: [9.1, 8.7],
  algeria: [28.0, 1.7], morocco: [31.8, -7.1], angola: [-11.2, 17.9],
  libya: [26.3, 17.2], kenya: [0.2, 37.9], ethiopia: [9.1, 40.5],
  ghana: [7.9, -1.0], tanzania: [-6.4, 34.9], tunisia: [33.9, 9.6],
  sudan: [12.9, 30.2], "ivory coast": [7.5, -5.5], senegal: [14.5, -14.5],
  cameroon: [7.4, 12.4], uganda: [1.4, 32.3], zambia: [-13.1, 27.8],
  zimbabwe: [-19.0, 29.2], mozambique: [-18.7, 35.5], botswana: [-22.3, 24.7],
  namibia: [-22.6, 17.1], "democratic republic of the congo": [-2.9, 23.7],
  "republic of the congo": [-0.7, 15.8], gabon: [-0.8, 11.6],
  "equatorial guinea": [1.5, 10.3], chad: [15.5, 18.7], niger: [17.6, 8.1],
  mali: [17.6, -4.0], "burkina faso": [12.2, -1.6], guinea: [9.9, -9.7],
  benin: [9.3, 2.3], togo: [8.6, 0.8], "sierra leone": [8.5, -11.8],
  liberia: [6.4, -9.4], mauritania: [21.0, -10.9], madagascar: [-18.8, 46.9],
  mauritius: [-20.3, 57.6], rwanda: [-1.9, 29.9], burundi: [-3.4, 29.9],
  malawi: [-13.3, 34.3], somalia: [5.2, 46.2], "south sudan": [7.3, 30.3],
  eritrea: [15.2, 39.8], djibouti: [11.8, 42.6], lesotho: [-29.6, 28.2],
  eswatini: [-26.5, 31.5], gambia: [13.4, -15.3], "guinea-bissau": [12.0, -15.0],
  "cape verde": [16.0, -24.0], comoros: [-11.6, 43.3], seychelles: [-4.7, 55.5],
  "sao tome and principe": [0.2, 6.6], "central african republic": [6.6, 20.9],
};

/** ISO 3166-1 alpha-2 → centroid key (the harvester's `country` field is often
 * a code like "AE"/"ZA"; map the ones that actually show up + the majors). */
const ISO2_TO_NAME: Record<string, string> = {
  US: "united states", CA: "canada", MX: "mexico", GB: "united kingdom",
  UK: "united kingdom", FR: "france", DE: "germany", ES: "spain", IT: "italy",
  NL: "netherlands", BE: "belgium", SE: "sweden", CH: "switzerland",
  AT: "austria", PL: "poland", UA: "ukraine", CZ: "czech republic",
  FI: "finland", NO: "norway", DK: "denmark", IE: "ireland", PT: "portugal",
  GR: "greece", HU: "hungary", RO: "romania", RU: "russia", AE: "united arab emirates",
  SA: "saudi arabia", QA: "qatar", KW: "kuwait", BH: "bahrain", OM: "oman",
  IL: "israel", TR: "turkey", IR: "iran", IQ: "iraq", CN: "china", JP: "japan",
  KR: "south korea", IN: "india", ID: "indonesia", PK: "pakistan", BD: "bangladesh",
  VN: "vietnam", TH: "thailand", PH: "philippines", MY: "malaysia", SG: "singapore",
  TW: "taiwan", HK: "hong kong", AU: "australia", NZ: "new zealand", BR: "brazil",
  AR: "argentina", CL: "chile", CO: "colombia", PE: "peru", VE: "venezuela",
  ZA: "south africa", EG: "egypt", NG: "nigeria", DZ: "algeria", MA: "morocco",
  KE: "kenya", ET: "ethiopia", GH: "ghana",
};

/** Common name spellings that aren't the canonical key. */
const NAME_ALIASES: Record<string, string> = {
  usa: "united states", "united states of america": "united states",
  "u.s.a.": "united states", america: "united states", uk: "united kingdom",
  "great britain": "united kingdom", england: "united kingdom",
  czechia: "czech republic", "south korea (republic of korea)": "south korea",
  "korea, south": "south korea", uae: "united arab emirates",
  "côte d'ivoire": "ivory coast", "cote d'ivoire": "ivory coast",
  "dr congo": "democratic republic of the congo", drc: "democratic republic of the congo",
  swaziland: "eswatini", "russian federation": "russia",
};

/** Resolve a centroid from the org's full `region` name or ISO-2 `country`. */
export function centroidFor(
  region: string | null | undefined,
  country: string | null | undefined,
): LatLng | null {
  for (const raw of [region, country]) {
    if (!raw) continue;
    const trimmed = raw.trim();
    if (!trimmed) continue;
    const lower = trimmed.toLowerCase();
    const viaName = COUNTRY_CENTROIDS[lower] ?? COUNTRY_CENTROIDS[NAME_ALIASES[lower] ?? ""];
    if (viaName) return viaName;
    const aliasFromIso = ISO2_TO_NAME[trimmed.toUpperCase()];
    if (aliasFromIso && COUNTRY_CENTROIDS[aliasFromIso]) return COUNTRY_CENTROIDS[aliasFromIso];
  }
  return null;
}

/** A human label for a country group, preferring the full region name. */
export function displayCountry(
  region: string | null | undefined,
  country: string | null | undefined,
): string {
  const r = (region ?? "").trim();
  if (r) return r;
  const c = (country ?? "").trim();
  if (!c) return "Unknown";
  // Turn a bare ISO-2 into its name if we know it; else title-case it.
  const named = ISO2_TO_NAME[c.toUpperCase()];
  if (named) return named.replace(/\b\w/g, (m) => m.toUpperCase());
  return c.length <= 3 ? c.toUpperCase() : c.replace(/\b\w/g, (m) => m.toUpperCase());
}
